import { useEffect, useState, FunctionComponent } from "react";
import { Col, Form, message, Modal, Popconfirm, Row, Spin } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  LoadingOutlined,
  QuestionCircleOutlined,
  SaveOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Field } from "@type/form/field.types";
import { ClientDetailsResponseDto } from "@state/clients/dto/response/client.details.response.dto";
import {
  requestCreateClient,
  requestDeleteClient,
  requestGetClient,
  requestUpdateClient,
} from "@state/clients/ClientEffects";
import { FIELD_LENGTH, formItemBlockLayout } from "@utils/Constant";
import { ClientSaveRequestDto } from "@state/clients/dto/request/client.save.request.dto";
import InputFormField from "@components/inputs/InputFormField";
import { ValidateErrorEntity, Store } from "rc-field-form/lib/interface";
import BasicButton from "@components/buttons/BasicButton";
import { hasPermission } from "@utils/permission-helper";

const { confirm } = Modal;

export interface Props {
  clientId?: string;
  editMode?: boolean;
  setEditMode?: (editMode: boolean) => void;
}

const ClientForm: FunctionComponent<Props> = (props: Props) => {
  const { clientId, editMode, setEditMode } = props;
  const { t } = useTranslation();

  const history = useHistory();

  const [form] = Form.useForm();

  const [client, setClient] = useState<ClientDetailsResponseDto | null>(null);

  const [fields, setFields] = useState<Field[]>([]);

  const [buttonLoading, setButtonLoading] = useState<boolean>(false);

  const [getResponseReceived, setGetResponseReceived] = useState<boolean>(
    false
  );

  const spinIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

  const confirmationPopup = async (action: string) => {
    return new Promise<boolean>((resolve) => {
      const nameField = form.getFieldValue("name") as string;

      confirm({
        title: t(`clients.edit.modals.${action}.title`),
        content: t(`clients.edit.modals.${action}.content`, {
          client: `${nameField}`,
        }),
        okText: t("buttons.yes"),
        cancelText: t("buttons.no"),
        centered: true,
        onOk() {
          resolve(true);
        },
        onCancel() {
          resolve(false);
        },
      });
    });
  };

  useEffect(() => {
    if (clientId) {
      void requestGetClient(clientId);
    }
  }, [clientId]);

  useEffect(() => {
    return requestGetClient.done.watch(({ result }) => {
      setGetResponseReceived(true);
      if (result.ok && result.data) {
        setClient(result.data);
        setFields([
          {
            name: ["name"],
            value: result.data.name,
          },
        ]);
      } else {
        void message.error(t("clients.notFound"));
      }
    });
  });

  useEffect(() => {
    return requestCreateClient.done.watch(({ result }) => {
      setButtonLoading(false);
      if (result.ok && result.data) {
        void message.success(t("clients.add.messages.success"));
        history.push("/clients");
      } else {
        void message.error(result.errorMessage);
      }
    });
  });

  useEffect(() => {
    return requestUpdateClient.done.watch(({ result }) => {
      setButtonLoading(false);
      if (result.ok && result.data) {
        setClient(result.data);
        void message.success(t("clients.edit.messages.success"));
        history.push("/clients");
      } else {
        void message.error(result.errorMessage);
      }
    });
  });

  useEffect(() => {
    return requestDeleteClient.done.watch(({ result }) => {
      setButtonLoading(false);
      if (result.ok) {
        void message.success(t("clients.delete.messages.success"));
        history.push("/clients");
      } else {
        void message.error(result.errorMessage);
      }
    });
  });

  const handleSubmit = (values: Store) => {
    void confirmationPopup("save").then((confirmed: boolean) => {
      if (confirmed) {
        setButtonLoading(true);
        const clientToSave: ClientSaveRequestDto = {
          name: values.name as string,
        };
        if (clientId) {
          void requestUpdateClient({
            id: clientId,
            dto: clientToSave,
          });
        } else {
          void requestCreateClient({
            dto: clientToSave,
          });
        }
      }
    });
  };

  const handleDelete = () => {
    if (clientId) {
      void confirmationPopup("delete").then((confirmed: boolean) => {
        if (confirmed) {
          void requestDeleteClient({
            id: clientId,
          });
        }
      });
    }
  };

  const onFinishFailed = ({ errorFields }: ValidateErrorEntity<Store>) => {
    void message.error(t("forms.errors.failed-validation"));
    form.scrollToField(errorFields[0].name);
  };

  return (
    <>
      {!getResponseReceived && clientId ? (
        <div style={{ textAlign: "center" }}>
          <Spin indicator={spinIcon} />
        </div>
      ) : !clientId || client ? (
        <>
          <Form
            {...formItemBlockLayout}
            form={form}
            onFinish={handleSubmit}
            onFinishFailed={onFinishFailed}
            fields={fields}
          >
            <div className="d-flex flex-column align-items-stretch flex-sm-row">
              <div className="w-100 d-flex justify-content-between mb-3 flex-column flex-sm-row">
                <h4 className="text-primary">{t("clients.title")}</h4>
                {hasPermission("clients", true) && (
                  <div className="d-flex align-items-center justify-content-center flex-wrap">
                    {editMode && setEditMode && (
                      <Popconfirm
                        title={t("forms.actions.modals.cancel.content")}
                        okText={t("buttons.yes")}
                        cancelText={t("buttons.no")}
                        onConfirm={() => {
                          setEditMode(false);
                        }}
                        placement="top"
                        icon={<QuestionCircleOutlined />}
                        className="m-2"
                      >
                        <BasicButton
                          type={"reset"}
                          variant={"default"}
                          text={t("buttons.cancel")}
                          icon={<StopOutlined />}
                          iconLeft={false}
                        />
                      </Popconfirm>
                    )}
                    {editMode ? (
                      <BasicButton
                        variant={"primary"}
                        onClick={form.submit}
                        text={t("buttons.save")}
                        icon={<SaveOutlined />}
                        iconLeft={false}
                        isLoading={buttonLoading}
                      />
                    ) : (
                      client &&
                      setEditMode && (
                        <>
                          <BasicButton
                            type={"reset"}
                            variant={"primary"}
                            text={t("buttons.edit")}
                            onClick={() => setEditMode(true)}
                            icon={<EditOutlined />}
                            iconLeft={false}
                            isLoading={buttonLoading}
                          />
                          <BasicButton
                            variant={"danger"}
                            text={t("buttons.delete")}
                            onClick={handleDelete}
                            icon={<DeleteOutlined />}
                            iconLeft={false}
                            isLoading={buttonLoading}
                            className={"ml-2"}
                          />
                        </>
                      )
                    )}
                  </div>
                )}
              </div>
            </div>
            <Row>
              <Col xs={24}>
                <InputFormField
                  showLabel
                  module="clients.edit"
                  field="name"
                  required={true}
                  emptyErrorMessage={t("forms.errors.mandatory")}
                  readOnly={!editMode}
                  maxLength={FIELD_LENGTH.NAME}
                />
              </Col>
            </Row>
          </Form>
        </>
      ) : (
        <div>{t("clients.notFound")}</div>
      )}
    </>
  );
};
export default ClientForm;
